/**
 * Hook to load chat message history + user reactions for a specific session.
 *
 * Response envelope (see src/space_operator/api/routes/chat_history.py):
 *   {
 *     messages: [{...message, reaction: {rating, comment, updated_at} | null}],
 *     session_reaction: {rating, comment, updated_at} | null
 *   }
 *
 * Exposes a ReactionDispatch so the mutation client (lib/feedback.ts) can write
 * optimistically with a pendingMap enforcing last-click-wins semantics.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatMessage } from '../types/chat'
import { fetchWithTimeout } from '../lib/http'
import {
  FEEDBACK_COMMENT_MAX,
  FeedbackRating,
  FeedbackReaction,
  SESSION_REACTION_KEY,
  reactionKey,
} from '../types/feedback'
import { supabase } from '../lib/supabase'
import type { ReactionDispatch } from '../lib/feedback'

export { FEEDBACK_COMMENT_MAX }

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export interface ChatMessageWithReaction extends ChatMessage {
  reaction: FeedbackReaction | null
}

interface HistoryEnvelope {
  messages: ChatMessageWithReaction[]
  session_reaction: FeedbackReaction | null
}

interface UseChatHistoryReturn {
  messages: ChatMessageWithReaction[]
  sessionReaction: FeedbackReaction | null
  loading: boolean
  error: string | null
  createDispatch: (messageId: string | null) => ReactionDispatch
}

interface PendingEntry {
  updatedAt: string
}

export function useChatHistory(sessionId: string | null): UseChatHistoryReturn {
  const [messages, setMessages] = useState<ChatMessageWithReaction[]>([])
  const [sessionReaction, setSessionReaction] = useState<FeedbackReaction | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pendingRef = useRef<Map<string, PendingEntry>>(new Map())

  useEffect(() => {
    if (!sessionId) return

    const fetchHistory = async () => {
      try {
        setLoading(true)
        setError(null)

        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        try {
          if (supabase) {
            const {
              data: { session },
            } = await supabase.auth.getSession()
            if (session?.access_token) {
              headers['Authorization'] = `Bearer ${session.access_token}`
            }
          }
        } catch {
          // Auth unavailable; dev mode proceeds without token.
        }

        const response = await fetchWithTimeout(`${API_BASE_URL}/api/chat/sessions/${sessionId}/messages`, { headers })

        if (response.status === 404) {
          setMessages([])
          setSessionReaction(null)
          return
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch history: ${response.status}`)
        }

        const data = (await response.json()) as HistoryEnvelope | ChatMessage[]

        // Backwards-compat safety: old server returns an array.
        if (Array.isArray(data)) {
          setMessages(data.map((m) => ({ ...m, reaction: null })))
          setSessionReaction(null)
        } else {
          setMessages(data.messages ?? [])
          setSessionReaction(data.session_reaction ?? null)
        }
      } catch (err) {
        console.error('[useChatHistory] Error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load history')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [sessionId])

  const setReactionForTarget = useCallback(
    (messageId: string | null, reaction: FeedbackReaction | null) => {
      if (messageId === null) {
        setSessionReaction(reaction)
        return
      }
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reaction } : m)))
    },
    [],
  )

  const getReactionForTarget = useCallback(
    (messageId: string | null): FeedbackReaction | null => {
      if (messageId === null) return sessionReaction
      return messages.find((m) => m.id === messageId)?.reaction ?? null
    },
    [messages, sessionReaction],
  )

  const createDispatch = useCallback(
    (messageId: string | null): ReactionDispatch => {
      const key = reactionKey(messageId)
      let rollbackSnapshot: FeedbackReaction | null = getReactionForTarget(messageId)

      return {
        optimistic(rating, comment) {
          rollbackSnapshot = getReactionForTarget(messageId)
          const updatedAt = new Date().toISOString()
          pendingRef.current.set(key, { updatedAt })
          if (rating === 'cleared') {
            setReactionForTarget(messageId, null)
          } else {
            setReactionForTarget(messageId, { rating, comment, updated_at: updatedAt })
          }
          return updatedAt
        },
        commit(clientUpdatedAt, rating, comment, serverUpdatedAt) {
          const pending = pendingRef.current.get(key)
          if (pending && pending.updatedAt !== clientUpdatedAt) {
            // A newer click is in flight; drop this response.
            return
          }
          pendingRef.current.delete(key)
          if (rating === 'cleared') {
            setReactionForTarget(messageId, null)
          } else {
            setReactionForTarget(messageId, { rating, comment, updated_at: serverUpdatedAt })
          }
        },
        rollback(clientUpdatedAt) {
          const pending = pendingRef.current.get(key)
          if (pending && pending.updatedAt === clientUpdatedAt) {
            pendingRef.current.delete(key)
            setReactionForTarget(messageId, rollbackSnapshot)
          }
        },
        currentRating(): FeedbackRating | null {
          const r = getReactionForTarget(messageId)
          return r?.rating ?? null
        },
        currentComment(): string | null {
          return getReactionForTarget(messageId)?.comment ?? null
        },
      }
    },
    [getReactionForTarget, setReactionForTarget],
  )

  return useMemo(
    () => ({ messages, sessionReaction, loading, error, createDispatch }),
    [messages, sessionReaction, loading, error, createDispatch],
  )
}

export { SESSION_REACTION_KEY }
